/**
 * lib/auth/useAccountProfileSync.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * Sprint: Auth Foundation V1 — logged-in language/currency preference sync.
 *
 * Contract (this sprint's own brief, Task 7):
 *   - Logged out: local preferences (usePreferences.ts / localStorage) are
 *     the whole story — this hook does nothing at all.
 *   - Logged in: on login, ensures a public.profiles row exists (seeded from
 *     current local prefs on first login), then adopts the CLOUD profile's
 *     preferred_language/preferred_currency into local state exactly ONCE
 *     per login IF they differ — this is what lets a returning user's
 *     preferences follow them to a new device/session.
 *   - From then on, any local language/currency change (from ANY UI —
 *     header dropdown, mobile prefs popover, the /cuenta page) is pushed
 *     to the cloud profile best-effort, fire-and-forget.
 *   - usePreferences() itself is untouched and remains the single source of
 *     truth for the CURRENT session's UI — it already updates instantly and
 *     synchronously on every set*() call; this hook only ever mirrors that
 *     value outward to Supabase afterward, never gates or delays it.
 *   - A sync failure (network, RLS, Supabase not configured) is silently
 *     swallowed by accountProfile.ts's own best-effort contract — it can
 *     never break marketplace search or any other part of the app.
 *
 * Mounted once, near the app root, inside <AuthProvider> (see
 * routes/__root.tsx) — never inside a route-specific component, so it
 * keeps syncing regardless of which page is currently showing.
 */
import { useEffect, useRef } from "react";
import { useAuth } from "@/lib/auth/AuthContext";
import { usePreferences } from "@/lib/hooks/usePreferences";
import { isSupabaseEnabled } from "@/lib/supabase";
import { ensureAccountProfile, updateAccountPreferences } from "@/lib/auth/accountProfile";

export function useAccountProfileSync(): void {
  const { user } = useAuth();
  const { language, currency, setLanguage, setCurrency } = usePreferences();

  // ── Adopt-on-login: runs once per user session, never on every local
  // preference change (deliberately keyed on user?.id alone — language/
  // currency are read via closure, not as dependencies). ───────────────────
  useEffect(() => {
    if (!user || !isSupabaseEnabled) return;
    let cancelled = false;

    void ensureAccountProfile(user.id, user.email ?? "", language, currency).then((profile) => {
      if (cancelled || !profile) return;
      // usePreferences' own setLanguage/setCurrency already validate and
      // fall back to a safe default internally — never trust the cloud
      // value blindly, but never need to re-validate it here either.
      if (profile.preferred_language && profile.preferred_language !== language) {
        setLanguage(profile.preferred_language);
      }
      if (profile.preferred_currency && profile.preferred_currency !== currency) {
        setCurrency(profile.preferred_currency);
      }
    });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // ── Push-on-change: best-effort mirror of local prefs to the cloud
  // profile, only while logged in. Idempotent (same value = harmless
  // no-op update) so this never fights the adopt-on-login effect above. ──
  const isFirstRunRef = useRef(true);
  useEffect(() => {
    if (!user || !isSupabaseEnabled) {
      isFirstRunRef.current = true;
      return;
    }
    // Skip the very first run right after a fresh login — the adopt-on-
    // login effect above already established the correct starting values;
    // pushing here too is harmless but redundant on every mount.
    if (isFirstRunRef.current) {
      isFirstRunRef.current = false;
      return;
    }
    void updateAccountPreferences(user.id, {
      preferred_language: language,
      preferred_currency: currency,
    });
  }, [user, language, currency]);
}
